import { useSubmit } from 'react-router-dom';
import {
  Avatar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@scholastic/mockingjay';

import { useGetMeApi } from 'eds-common/api/v2';
import {
  useSwitchOrganization,
  type UserOrg,
} from '@/hooks/use-switch-organization/use-switch-organization';
import { SwitchingOverlay } from '@/components/ProfileMenu/SwitchingOverlay';

interface OrgSubMenuProps {
  orgs: UserOrg[];
  currentOrgName: string;
  onSelectOrg: (orgId: string, orgName: string) => void;
}

function OrgSubMenu({ orgs, currentOrgName, onSelectOrg }: OrgSubMenuProps) {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="pl-5">
        Switch Organizations
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent
        collisionPadding={16}
        className="relative max-h-[65vh] overflow-y-auto"
      >
        <DropdownMenuLabel className="text-base font-semibold text-neutral-1300">
          Switch Organization
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup value={currentOrgName}>
          {orgs.map(({ orgId, orgName }) =>
            orgName ? (
              <DropdownMenuRadioItem
                key={orgId}
                value={orgName}
                className="flex items-center"
                onSelect={() => onSelectOrg(orgId, orgName)}
              >
                {orgName}
              </DropdownMenuRadioItem>
            ) : null
          )}
        </DropdownMenuRadioGroup>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

export function ProfileMenu() {
  const { data: user } = useGetMeApi();
  const submit = useSubmit();
  const { switchingToOrgName, switchOrg, orgs, canSwitch } =
    useSwitchOrganization();

  const onSignOut = () => {
    submit(null, { method: 'post', action: '/sign-out' });
  };

  if (!user) {
    return null;
  }

  return (
    <>
      {switchingToOrgName && <SwitchingOverlay orgName={switchingToOrgName} />}
      <DropdownMenu>
        <DropdownMenuTrigger>
          <button
            className="cursor-pointer rounded-full focus-ring"
            type="button"
            aria-label="profile"
          >
            <Avatar
              name={`${user.givenName} ${user.familyName}`}
              variant="medium"
              className="bg-neutral-100 uppercase"
            />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          className="space-y-4 rounded-lg bg-white shadow-[0_4px_24px_0_rgba(0,0,0,0.25)]"
          align="end"
        >
          <div className="flex items-center gap-2 p-4">
            <Avatar
              name={`${user.givenName} ${user.familyName}`}
              variant="medium"
              className="bg-neutral-100 uppercase"
            />
            <div className="max-w-[240px] bg-brand-white">
              <p
                role="presentation"
                className="truncate font-semibold capitalize text-neutral-1300 "
              >{`${user.givenName} ${user.familyName}`}</p>
              <p
                role="presentation"
                className="truncate text-sm capitalize text-neutral-900"
              >{`${user.currentRole}`}</p>
            </div>
          </div>
          {canSwitch && (
            <OrgSubMenu
              orgs={orgs}
              currentOrgName={user.currentOrg.orgName}
              onSelectOrg={switchOrg}
            />
          )}
          <DropdownMenuSeparator aria-hidden="true" />
          <DropdownMenuItem className="pl-5" onSelect={onSignOut}>
            Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
